import { SFX_DURATION_POLICY } from '../../../../generated/nodeDataTypes'

/**
 * Whether an `in_duration` string is legal for sfx mode.
 *
 * Bounds come from the GENERATED SFX_DURATION_POLICY, not a local copy, so this
 * cannot drift from what the backend actually refuses.
 *
 * Only recognises the shapes the duration pickers can produce ('auto' and
 * '<int>s'). Anything unparsed is treated as legal on purpose: this guard
 * exists to catch a known-bad carry-over, and it must never be the thing that
 * discards a value it simply failed to understand.
 */
export function isDurationWithinSfxBound(raw: string | undefined): boolean {
  const text = (raw ?? '').trim().toLowerCase()
  if (text === '' || text === 'auto') return true
  const match = /^(\d+)\s*s?$/.exec(text)
  if (!match) return true
  const seconds = Number(match[1])
  return seconds >= SFX_DURATION_POLICY.minSeconds && seconds <= SFX_DURATION_POLICY.maxSeconds
}

export type WaveformPreviewAction = 'open_config' | 'seek'

export function getWaveformPreviewAction(options: {
  hasAudio: boolean
  selected: boolean
}): WaveformPreviewAction {
  const { hasAudio, selected } = options
  if (!hasAudio || !selected) {
    return 'open_config'
  }
  return 'seek'
}

export type WaveformPreviewClickDecision = 'passthrough' | 'open_config'

/**
 * Decide what a capture-phase click on the waveform preview area should do.
 *
 * The waveform wrapper handles clicks in the CAPTURE phase so that, on an
 * unselected node, tapping the preview selects the node + opens Config instead
 * of seeking. But that capture handler encloses the play/pause control too, and
 * calling `stopPropagation()` in the capture phase kills the click before it can
 * reach the button's own `onClick` (`togglePlayback`). That made the play button
 * appear dead until the node was selected — the user had to "click outside
 * first" (Workplace post 1572398761184088).
 *
 * Fix: a click that lands on the play/pause control must ALWAYS pass through to
 * the audio player, regardless of selection. Only non-button clicks on an
 * unselected node (`action === 'open_config'`) are intercepted to open Config.
 */
export function resolveWaveformPreviewClickDecision(options: {
  action: WaveformPreviewAction
  targetIsPlayButton: boolean
}): WaveformPreviewClickDecision {
  const { action, targetIsPlayButton } = options
  if (targetIsPlayButton) return 'passthrough'
  return action === 'open_config' ? 'open_config' : 'passthrough'
}

export function sortAudioVariantsNewestFirst<T extends { index: number }>(variants: T[]): T[] {
  return [...variants].sort((a, b) => b.index - a.index)
}
